import { useEffect, useMemo, useState } from "react";

import { useMediaQuery } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid";
import TilleyDataGrid from "components/shared/DataGrid/TilleyDataGrid";
import { CustomWidthTooltip } from "mui-toolkit/CustomWidthTooltip";
import DeleteConfirmationButton from "mui-toolkit/DeleteConfirmationButton";
import { formatPrice } from "utility/formatters";

import { formatDate } from "../../utility/formatters";
import { NavigateIcon } from "../Icons/Icons";

export default function EquipmentRepairDataTable({
  repairs = [],
  onEdit,
  onDelete,
  isLoading,
  pageSizes = [5, 10, 25, 50, 100],
  rowHeight = 80,
}) {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("lg"), { noSsr: true });
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const columns = useMemo(
    () => [
      {
        field: "repair_date",
        headerName: "Date",
        flex: 1.2,
        valueGetter: (paramsOrValue, rowOrUndefined) => {
          const row = paramsOrValue?.row || rowOrUndefined || {};
          return row.repair_date || "";
        },
        valueFormatter: (value) => (value ? formatDate(value) : ""),
      },
      {
        field: "description",
        headerName: "Description",
        flex: 2,
      },
      {
        field: "parts",
        headerName: "Parts",
        flex: 1,
        valueGetter: (paramsOrValue, rowOrUndefined) => {
          const row = paramsOrValue?.row || rowOrUndefined || {};
          return row.parts || "";
        },
        valueFormatter: (value) => {
          if (!value || value === "") return "-";
          const numValue = Number(value) || 0;
          return formatPrice(numValue, 0);
        },
      },
      {
        field: "labor",
        headerName: "Labor",
        flex: 1,
        valueGetter: (paramsOrValue, rowOrUndefined) => {
          const row = paramsOrValue?.row || rowOrUndefined || {};
          return row.labor || "";
        },
        valueFormatter: (value) => {
          if (!value || value === "") return "-";
          const numValue = Number(value) || 0;
          return formatPrice(numValue, 0);
        },
      },
      {
        field: "total",
        headerName: "Total",
        flex: 1,
        valueGetter: (paramsOrValue, rowOrUndefined) => {
          const row = paramsOrValue?.row || rowOrUndefined || {};
          const parts = Number(row?.parts) || 0;
          const labor = Number(row?.labor) || 0;
          return parts + labor;
        },
        valueFormatter: (value) => formatPrice(value || 0, 0),
      },
      {
        field: "actions",
        headerName: "Actions",
        type: "actions",
        headerAlign: "right",
        align: "right",
        width: 150,
        sortable: false,
        getActions: (params) => {
          return [
            <>
              <CustomWidthTooltip key="delete" title="Delete" placement="top" arrow>
                <DeleteConfirmationButton item={params.row} onChange={onDelete} />
              </CustomWidthTooltip>

              <GridActionsCellItem
                key="edit"
                icon={<NavigateIcon />}
                label="Edit"
                data-testid={`edit-repair-${params.row.id}`}
                onClick={() => onEdit?.(params.row)}
              />
            </>,
          ];
        },
        flex: 0,
      },
    ],
    [onEdit, onDelete]
  );

  if (!isMounted) {
    return null;
  }

  return (
    <>
      <TilleyDataGrid
        rows={repairs || []}
        columns={columns}
        pageSizeOptions={pageSizes}
        defaultPageSize={isMobile ? 5 : 10}
        gridKey="equipment-repairs"
        getRowId={(row) => row.id}
        getRowHeight={() => rowHeight}
        isLoading={isLoading}
        sx={{ width: "99%", boxSizing: "border-box" }}
      />
    </>
  );
}
